import { useState, useEffect, useCallback, useRef } from 'react';
import { X, Key, Plus, Trash2, AlertTriangle, CheckCircle, Shield } from 'lucide-react';
import { startRegistration } from '@simplewebauthn/browser';
import { colors } from '../styles/colors';
import { NETLIFY_FUNCTIONS } from '../config/api';
import { useAuth } from '../contexts/AuthContext';

function PasskeyModal({ onClose }) {
  const { getToken } = useAuth();
  const [passkeys, setPasskeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [newKeyName, setNewKeyName] = useState('');
  const mouseDownOnBackdrop = useRef(false);
  const [showAddForm, setShowAddForm] = useState(false);

  const authHeaders = {
    'Authorization': `Bearer ${getToken()}`,
    'Content-Type': 'application/json',
  };

  const loadPasskeys = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(NETLIFY_FUNCTIONS.WEBAUTHN_LIST_PASSKEYS, {
        method: 'GET',
        headers: authHeaders,
      });
      if (!res.ok) throw new Error('Impossible de charger les clés');
      const data = await res.json();
      setPasskeys(data.passkeys || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line

  useEffect(() => {
    loadPasskeys();
  }, []); // eslint-disable-line

  const handleAdd = async () => {
    setActionLoading(true);
    setError(null);
    setSuccess(null);
    try {
      // 1. Obtenir les options d'enregistrement
      const optRes = await fetch(NETLIFY_FUNCTIONS.WEBAUTHN_REGISTER_OPTIONS, {
        method: 'GET',
        headers: authHeaders,
      });
      if (!optRes.ok) {
        const d = await optRes.json();
        throw new Error(d.error || 'Impossible d\'obtenir les options');
      }
      const options = await optRes.json();

      // 2. Déclencher la création de la clé dans le navigateur
      let registrationResponse;
      try {
        registrationResponse = await startRegistration(options);
      } catch (browserErr) {
        if (browserErr.name === 'InvalidStateError') {
          throw new Error('Cette clé est déjà enregistrée sur ce compte');
        }
        if (browserErr.name === 'NotAllowedError') {
          throw new Error('Enregistrement annulé');
        }
        throw new Error('Erreur lors de la création de la clé : ' + browserErr.message);
      }

      // 3. Vérifier côté serveur et sauvegarder
      const verifyRes = await fetch(NETLIFY_FUNCTIONS.WEBAUTHN_REGISTER_VERIFY, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({
          response: registrationResponse,
          name: newKeyName.trim() || 'Clé de sécurité',
        }),
      });
      const verifyData = await verifyRes.json();
      if (!verifyRes.ok) throw new Error(verifyData.error || 'Enregistrement échoué');

      setSuccess('Clé enregistrée avec succès !');
      setNewKeyName('');
      setShowAddForm(false);
      // Recharger la liste
      await loadPasskeys();
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async (passkeyId) => {
    if (!window.confirm('Supprimer cette clé de sécurité ?')) return;
    setActionLoading(true);
    setError(null);
    try {
      const res = await fetch(NETLIFY_FUNCTIONS.WEBAUTHN_REGISTER_VERIFY, {
        method: 'DELETE',
        headers: authHeaders,
        body: JSON.stringify({ passkeyId }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Suppression échouée');
      setPasskeys((prev) => prev.filter((p) => p.id !== passkeyId));
      setSuccess('Clé supprimée');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (isoString) => {
    if (!isoString) return '—';
    return new Date(isoString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    });
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      onMouseDown={(e) => { mouseDownOnBackdrop.current = e.target === e.currentTarget; }}
      onClick={(e) => { if (e.target === e.currentTarget && mouseDownOnBackdrop.current) onClose(); }}
    >
      <div
        style={{
          backgroundColor: colors.text.white,
          borderRadius: '1rem',
          width: '90%',
          maxWidth: '480px',
          boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '1.25rem 1.5rem',
            backgroundColor: colors.primary.teal,
            borderRadius: '1rem 1rem 0 0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <Key size={24} color={colors.text.white} />
            <h2
              style={{
                margin: 0,
                fontFamily: '"Century Gothic Bold", "Noto Sans", sans-serif',
                fontSize: '1.25rem',
                color: colors.text.white,
                fontWeight: 'bold',
              }}
            >
              Mes clés de sécurité
            </h2>
          </div>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0.25rem' }}
          >
            <X size={24} color={colors.text.white} />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '1.5rem', overflowY: 'auto', flex: 1 }}>

          {/* Info box */}
          <div
            style={{
              display: 'flex',
              gap: '0.75rem',
              padding: '0.875rem 1rem',
              backgroundColor: `${colors.primary.teal}15`,
              borderRadius: '0.5rem',
              marginBottom: '1.25rem',
              alignItems: 'flex-start',
            }}
          >
            <Shield size={18} color={colors.primary.teal} style={{ flexShrink: 0, marginTop: '1px' }} />
            <p
              style={{
                margin: 0,
                fontSize: '0.8125rem',
                color: colors.primary.gray,
                fontFamily: '"Noto Sans", sans-serif',
                lineHeight: '1.5',
              }}
            >
              Les clés de sécurité (passkeys) permettent de vous connecter sans mot de passe via Windows Hello, votre empreinte digitale ou un YubiKey.
            </p>
          </div>

          {/* Messages */}
          {error && (
            <div
              style={{
                padding: '0.875rem 1rem',
                borderRadius: '0.5rem',
                backgroundColor: '#fee2e2',
                border: '1px solid #ef4444',
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                marginBottom: '1rem',
              }}
            >
              <AlertTriangle size={18} color="#dc2626" />
              <span style={{ color: '#991b1b', fontSize: '0.875rem' }}>{error}</span>
            </div>
          )}

          {success && (
            <div
              style={{
                padding: '0.875rem 1rem',
                borderRadius: '0.5rem',
                backgroundColor: '#d1fae5',
                border: '1px solid #10b981',
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                marginBottom: '1rem',
              }}
            >
              <CheckCircle size={18} color="#065f46" />
              <span style={{ color: '#065f46', fontSize: '0.875rem' }}>{success}</span>
            </div>
          )}

          {/* Liste des clés */}
          {loading ? (
            <p style={{ textAlign: 'center', color: colors.secondary.mediumGray, fontFamily: '"Noto Sans", sans-serif' }}>
              Chargement...
            </p>
          ) : passkeys.length === 0 ? (
            <p
              style={{
                textAlign: 'center',
                color: colors.secondary.mediumGray,
                fontFamily: '"Noto Sans", sans-serif',
                fontSize: '0.9rem',
                padding: '1rem 0',
              }}
            >
              Aucune clé enregistrée
            </p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: '1rem' }}>
              {passkeys.map((pk) => (
                <div
                  key={pk.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '0.875rem 1rem',
                    border: `1px solid ${colors.secondary.lightTeal}`,
                    borderRadius: '0.5rem',
                    backgroundColor: colors.secondary.lightGray,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <Key size={20} color={colors.primary.teal} />
                    <div>
                      <p
                        style={{
                          margin: 0,
                          fontWeight: '600',
                          fontSize: '0.9rem',
                          color: colors.primary.gray,
                          fontFamily: '"Noto Sans", sans-serif',
                        }}
                      >
                        {pk.name || 'Clé de sécurité'}
                      </p>
                      {pk.created_at && (
                        <p
                          style={{
                            margin: '0.15rem 0 0 0',
                            fontSize: '0.75rem',
                            color: colors.secondary.mediumGray,
                            fontFamily: '"Noto Sans", sans-serif',
                          }}
                        >
                          Ajoutée le {formatDate(pk.created_at)}
                          {pk.last_used_at && ` · Utilisée le ${formatDate(pk.last_used_at)}`}
                        </p>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => handleDelete(pk.id)}
                    disabled={actionLoading}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: actionLoading ? 'not-allowed' : 'pointer',
                      padding: '0.375rem',
                      borderRadius: '0.375rem',
                      display: 'flex',
                      alignItems: 'center',
                      color: '#dc2626',
                      opacity: actionLoading ? 0.5 : 1,
                    }}
                    title="Supprimer cette clé"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Formulaire d'ajout */}
          {showAddForm ? (
            <div
              style={{
                padding: '1rem',
                border: `1.5px dashed ${colors.primary.teal}`,
                borderRadius: '0.5rem',
                display: 'flex',
                flexDirection: 'column',
                gap: '0.75rem',
              }}
            >
              <p
                style={{
                  margin: 0,
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  color: colors.primary.gray,
                  fontFamily: '"Noto Sans", sans-serif',
                }}
              >
                Nom de la clé (optionnel)
              </p>
              <input
                type="text"
                placeholder="Ex : Mon PC Bureau"
                value={newKeyName}
                onChange={(e) => setNewKeyName(e.target.value)}
                maxLength={50}
                style={{
                  width: '100%',
                  padding: '0.625rem 0.75rem',
                  border: `1.5px solid ${colors.secondary.lightTeal}`,
                  borderRadius: '0.375rem',
                  fontSize: '0.9rem',
                  fontFamily: '"Noto Sans", sans-serif',
                  color: colors.primary.gray,
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
                onKeyDown={(e) => { if (e.key === 'Enter') handleAdd(); }}
              />
              <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => { setShowAddForm(false); setNewKeyName(''); setError(null); }}
                  disabled={actionLoading}
                  style={{
                    padding: '0.5rem 1rem',
                    borderRadius: '0.375rem',
                    border: `1.5px solid ${colors.secondary.mediumGray}`,
                    backgroundColor: 'transparent',
                    color: colors.primary.gray,
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    cursor: 'pointer',
                    fontFamily: '"Noto Sans", sans-serif',
                  }}
                >
                  Annuler
                </button>
                <button
                  onClick={handleAdd}
                  disabled={actionLoading}
                  style={{
                    padding: '0.5rem 1.25rem',
                    borderRadius: '0.375rem',
                    border: 'none',
                    backgroundColor: actionLoading ? colors.secondary.mediumGray : colors.primary.teal,
                    color: colors.text.white,
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    cursor: actionLoading ? 'not-allowed' : 'pointer',
                    fontFamily: '"Noto Sans", sans-serif',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                  }}
                >
                  {actionLoading ? 'En cours...' : 'Créer la clé'}
                </button>
              </div>
            </div>
          ) : (
            <button
              onClick={() => { setShowAddForm(true); setError(null); setSuccess(null); }}
              disabled={actionLoading}
              style={{
                width: '100%',
                padding: '0.75rem',
                borderRadius: '0.5rem',
                border: `1.5px dashed ${colors.primary.teal}`,
                backgroundColor: 'transparent',
                color: colors.primary.teal,
                fontSize: '0.9rem',
                fontWeight: '500',
                cursor: 'pointer',
                fontFamily: '"Noto Sans", sans-serif',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem',
              }}
            >
              <Plus size={18} />
              Ajouter une clé de sécurité
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default PasskeyModal;
